import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useTrackedPersonnel from 'components/hooks/useTrackedPersonnel';

import BlockList from 'components/application/BlockList';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import CourseBookedProgressPill from 'components/courses/pills/CourseBookedProgressPill';
import ELearningCourseAutoEnrolsPill from 'components/courses/pills/ELearningCourseAutoEnrolsPill';
import PersonnelCourseTrainingStatusIndicator from 'components/personnel/PersonnelCourseTrainingStatusIndicator';

import { subcontractorDisplayName } from 'components/helpers/companies';
import { formatDate } from 'components/helpers/dates';
import {
  autoEnrolmentNonCriticalBlockersStatusText,
  hasOnlyNonCriticalBlockers,
  prioritisedAutoEnrolmentBlockers as prioritisedAutoEnrolmentBlockersList,
  proposedAutoEnrolmentDate,
} from 'components/helpers/resources/autoEnrolments';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function CourseSidePanelPersonnelTab({
  autoEnrolmentConfiguration,
  course,
  domainCourse,
  eLearningCourse,
  isLoadMorePersonnelActive,
  loadMoreRef,
  personnel,
}) {
  const currentActor = useCurrentActor();

  const [eLearningAllowance, setELearningAllowance] = useState({
    loaded: false,
    currentCredits: null,
    hasError: false,
  });

  const { hasReachedTrackedPersonnelLimit } = useTrackedPersonnel();

  const {
    hasPersonnelViewableAccess,
    isAutoEnrolmentIncludingMissingTraining,
  } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.sidepanel.CourseSidePanelPersonnelTab',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { t: i18nTHelpers } = useTranslation('components', {
    keyPrefix: 'helpers.resources.autoEnrolments',
  });

  useEffect(() => {
    if (
      currentActor.isAllowedFeature([
        'e_learning_auto_enrol',
        'e_learning',
        'training_register',
      ])
    ) {
      fetchELearningAllowance();
    }
  }, []);

  const fetchELearningAllowance = () => {
    axios
      .get('/e_learning/allowance')
      .then((response) =>
        setELearningAllowance({
          ...eLearningAllowance,
          loaded: true,
          currentCredits: response.data.data.attributes.currentCredits,
        }),
      )
      .catch(() =>
        setELearningAllowance({ ...eLearningAllowance, hasError: true }),
      );
  };

  const personnelBookingsMap = personnel.includedBookings.reduce(
    (acc, booking) => {
      acc[booking.relationships.personnel.data.id] = booking;
      return acc;
    },
    {},
  );

  const personnelProfilePhotosMap = personnel.includedProfilePhotos.reduce(
    (acc, profilePhoto) => {
      acc[profilePhoto.relationships.personnel.data.id] = profilePhoto;
      return acc;
    },
    {},
  );

  let personnelRegistrationsMap = {};
  if (course.isELearningProviderSelected) {
    personnelRegistrationsMap = personnel.includedRegistrations.reduce(
      (acc, registration) => {
        acc[registration.relationships.personnel.data.id] = registration;
        return acc;
      },
      {},
    );
  }

  const personnelTrainingsMap = personnel.collection.reduce((acc, person) => {
    const matchingTraining = personnel.includedTrainings.find(
      (trainingItem) =>
        trainingItem.relationships.personnel.data.id === person.id,
    );
    acc[person.id] = matchingTraining;
    return acc;
  }, {});

  const autoEnrolmentExclusionsMap =
    personnel.includedAutoEnrolmentExclusions.reduce(
      (acc, autoEnrolmentExclusion) => {
        acc[autoEnrolmentExclusion.relationships.personnel.data.id] =
          autoEnrolmentExclusion;
        return acc;
      },
      {},
    );

  return (
    <div key='personnelTab' label='personnel'>
      {personnel.loaded && personnel.collection.length === 0 ?
        <li className='block-list__item tw-border-grey-100'>
          <div className='block-list__item--flex-container'>
            <div className='block-list__item--align-center'>
              <span>{i18nT('emptyState')}</span>
            </div>
          </div>
        </li>
      : <>
          <BlockList
            blockListItemClasses={(person) =>
              person.relationships.subcontractor.data ?
                ' block-list__item--triple'
              : ''
            }
            collection={personnel.collection}
            onItemClick={
              hasPersonnelViewableAccess ?
                (person) => window.location.replace(`personnel/${person.id}`)
              : undefined
            }
            onItemDisplay={(person) => {
              const booking = personnelBookingsMap[person.id];

              let registration;
              let autoEnrolmentExclusion;
              if (course.isELearningProviderSelected) {
                registration = personnelRegistrationsMap[person.id];
                autoEnrolmentExclusion = autoEnrolmentExclusionsMap[person.id];
              }

              const latestTraining = personnelTrainingsMap[person.id];
              const profilePhoto = personnelProfilePhotosMap[person.id];

              const eLearningAutoEnrolmentPill =
                (function eLearningAutoEnrolmentPill() {
                  if (
                    currentActor.isAllowedFeature([
                      'e_learning_auto_enrol',
                      'e_learning',
                      'training_register',
                    ]) &&
                    eLearningCourse &&
                    !booking &&
                    !autoEnrolmentExclusion
                  ) {
                    const prioritisedAutoEnrolmentBlockers =
                      prioritisedAutoEnrolmentBlockersList({
                        isELearningNonCustom:
                          eLearningCourse && !eLearningCourse.attributes.custom,
                        isCreditInfoUnavailable: eLearningAllowance.hasError,
                        isPersonnelEmailUnavailable: !person.attributes.email,
                        currentELearningCredits:
                          eLearningAllowance.loaded &&
                          eLearningAllowance.currentCredits,
                        isAutoEnrolmentIncludingMissingTraining,
                        training: latestTraining,
                        isBookingAllowedWithinTrackedPersonnelLimits:
                          !hasReachedTrackedPersonnelLimit ||
                          person.meta.isTracked,
                        autoEnrolmentConfiguration,
                        isExpiringCourse: domainCourse.attributes.expires,
                        isSubcontractor:
                          !!person.relationships.subcontractor.data,
                        isRequired: person.meta?.isCourseRequired,
                        isFromAutoEnrollableDivision:
                          person.meta?.isFromAutoEnrollableDivision,
                      });

                    const hasNoBlockers =
                      prioritisedAutoEnrolmentBlockers.length === 0;
                    const hasNonCriticalBlockers = hasOnlyNonCriticalBlockers({
                      prioritisedAutoEnrolmentBlockers,
                    });

                    if (hasNoBlockers || hasNonCriticalBlockers) {
                      const proposedEnrolmentDate = proposedAutoEnrolmentDate({
                        courseExpiringDurationParts:
                          domainCourse.attributes.expiringDurationParts,
                        latestTraining,
                      });

                      return (
                        <ELearningCourseAutoEnrolsPill
                          color={hasNonCriticalBlockers ? 'amber' : 'green'}
                          tooltipText={autoEnrolmentNonCriticalBlockersStatusText(
                            {
                              priorityBlocker:
                                prioritisedAutoEnrolmentBlockers[0],
                              proposedEnrolmentDate,
                              translations: i18nTHelpers(
                                'autoEnrolmentNonCriticalBlockersStatusText',
                                {
                                  date:
                                    proposedEnrolmentDate &&
                                    formatDate({
                                      date: proposedEnrolmentDate,
                                      format: 'd MMM yy',
                                    }),
                                  returnObjects: true,
                                },
                              ),
                            },
                          )}
                        />
                      );
                    }
                  }
                })();

              const trainingDescription = (function trainingDescription() {
                switch (person.attributes.trainingStatus) {
                  case 0:
                  case 1:
                    return i18nTCommon('shared.missing');
                  case 2:
                    return course.expires ?
                        i18nTCommon('shared.expiredDate', {
                          date: formatDate({
                            date: latestTraining.attributes.expiryDate,
                            format: 'dd MMM yyyy',
                          }),
                        })
                      : i18nTCommon('shared.doesNotExpire');
                  case 3:
                  case 4:
                    return course.expires ?
                        i18nTCommon('shared.expiresDate', {
                          date: formatDate({
                            date: latestTraining.attributes.expiryDate,
                            format: 'dd MMM yyyy',
                          }),
                        })
                      : i18nTCommon('shared.doesNotExpire');
                  case 5:
                    if (!latestTraining) {
                      return i18nT(
                        'trainingDescriptions.missingButNotRequired',
                      );
                    } else if (course.expires) {
                      const { expiryDate } = latestTraining.attributes;

                      const hasExpired = new Date() > new Date(expiryDate);

                      return i18nT(
                        hasExpired ?
                          'trainingDescriptions.expiredButNotRequired'
                        : 'trainingDescriptions.expiresButNotRequired',
                        {
                          date: formatDate({
                            date: expiryDate,
                            format: 'dd MMM yyyy',
                          }),
                        },
                      );
                    } else {
                      return i18nTCommon('shared.doesNotExpire');
                    }
                }
              })();

              return (
                <div className='block-list__item--flex-container'>
                  <div className='tw-flex tw-items-center'>
                    <div className='tw-mr-3'>
                      <PersonnelCourseTrainingStatusIndicator
                        status={person.attributes.trainingStatus}
                      />
                    </div>
                    <ProfilePhotoDisplay
                      componentSize='sm'
                      photoUrl={profilePhoto?.links.croppedUrl}
                      profileInitials={`${person.attributes.firstName[0]}${person.attributes.lastName[0]}`}
                    />
                  </div>

                  <div className='block-list__item--text tw-max-w-[300px]'>
                    <div className='tw-flex tw-max-w-[300px]'>
                      <span className='truncated-text-container truncated-text-container--width-auto'>
                        {`${person.attributes.firstName} ${person.attributes.lastName}`}
                      </span>
                      {booking && (
                        <CourseBookedProgressPill
                          booking={booking}
                          isELearningProviderSelected={
                            course.isELearningProviderSelected
                          }
                          registration={registration}
                        />
                      )}
                      {eLearningAutoEnrolmentPill}
                    </div>
                    {person.relationships.subcontractor.data && (
                      <span className='block-list__item--sub-text-triple truncated-text-container tw-text-s tw-tracking-wide tw-text-grey-500'>
                        {subcontractorDisplayName(
                          person.relationships.subcontractor.data.id,
                          personnel.includedSubcontractors,
                        )}
                      </span>
                    )}
                    <span className='block-list__item--sub-text-triple truncated-text-container tw-text-s tw-tracking-wide tw-text-grey-500'>
                      {trainingDescription}
                    </span>
                  </div>
                </div>
              );
            }}
          />
          {isLoadMorePersonnelActive && !personnel.meta.isLastPage && (
            <li
              className='block-list__item tw-border-grey-100'
              ref={loadMoreRef}
            >
              <div className='block-list__item--flex-container'>
                <div className='block-list__item--align-center'>
                  <span>{i18nTCommon('personnel.loadingPersonnel')}</span>
                </div>
              </div>
            </li>
          )}
        </>
      }
    </div>
  );
}

CourseSidePanelPersonnelTab.propTypes = {
  autoEnrolmentConfiguration: resourceShape('autoEnrolmentConfiguration'),
  course: PropTypes.object.isRequired,
  domainCourse: resourceShape('course').isRequired,
  isLoadMorePersonnelActive: PropTypes.bool.isRequired,
  loadMoreRef: PropTypes.func.isRequired,
  personnel: PropTypes.object.isRequired,
};
